"use client";

import { ArrowRight, BookOpen, Droplets } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { BucketIcon } from "@/components/ui/bucket-icon";
import { NavLink } from "@/components/ui/nav-link";

interface StartScreenProps {
  hasSavedProgress: boolean;
  isQuizComplete: boolean;
  onReset: () => void;
}

export function StartScreen({
  hasSavedProgress,
  isQuizComplete,
  onReset,
}: StartScreenProps) {
  const t = useTranslations();

  return (
    <div
      className="flex flex-col items-center justify-center min-h-[75vh] text-center px-4 pb-8 relative"
      data-testid="start-screen"
    >
      <div className="absolute inset-0 bg-mesh-gradient pointer-events-none" />
      
      <div className="relative z-10 flex flex-col items-center max-w-2xl mx-auto">
        <div className="relative mt-8 mb-10 opacity-0 animate-reveal-up" data-testid="start-hero-icon">
          <div className="absolute -inset-6 rounded-full bg-primary/5 animate-pulse-soft" />
          <div className="absolute -inset-12 rounded-full bg-primary/3 animate-pulse-soft" style={{ animationDelay: '1s' }} />
          <div className="relative">
            <BucketIcon className="w-32 h-32 text-primary animate-float" fillLevel={0.65} />
            <Droplets className="absolute -top-1 -right-1 w-6 h-6 text-accent animate-pulse" />
          </div>
        </div>

        <div className="space-y-5 max-w-xl mx-auto opacity-0 animate-reveal-up stagger-1">
          <h1 
            className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight text-foreground leading-[1.1]" 
            data-testid="start-title"
          >
            <span className="text-gradient">{t("title")}</span>
          </h1>
          <p 
            className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-md mx-auto font-light" 
            data-testid="start-description"
          >
            {t("description")}
          </p>
        </div>

        <div 
          className="flex flex-col sm:flex-row gap-4 w-full max-w-md mt-10 opacity-0 animate-reveal-up stagger-2" 
          data-testid="start-actions"
        >
          <NavLink
            href="/quiz/instructions"
            onClick={hasSavedProgress ? onReset : undefined}
            className="flex-1 btn-primary text-primary-foreground h-14 px-8 rounded-2xl font-semibold text-lg transform active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2"
            data-testid="start-new-btn"
          >
            {hasSavedProgress ? t("start_btn_new") : t("start_btn")}
            <ArrowRight className="w-5 h-5" />
          </NavLink>

          {hasSavedProgress && (
            <NavLink
              href={isQuizComplete ? "/results" : "/quiz"}
              className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 h-14 px-6 rounded-2xl font-semibold text-lg transition-all border border-border hover:border-primary/20 cursor-pointer flex items-center justify-center"
              data-testid="continue-btn"
            >
              {isQuizComplete ? t("view_results_btn") : t("continue_btn")}
            </NavLink>
          )}
        </div>

        <NavLink
          href="/guide"
          className="w-full max-w-2xl mt-16 cursor-pointer group mx-auto opacity-0 animate-reveal-up stagger-3 block"
          data-testid="guide-card"
          aria-label={t("guide_card_title")}
        >
          <div className="bg-card border border-border rounded-3xl overflow-hidden card-elevated group-hover:card-glow transition-all duration-500 ease-out transform group-hover:-translate-y-2 flex flex-col sm:flex-row h-auto min-h-[220px] relative">
            <div className="w-full h-52 sm:h-auto sm:w-2/5 relative bg-secondary shrink-0 overflow-hidden">
              <Image
                src="/images/couple-small.webp"
                alt={t("guide_card_image_alt")}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-110"
                priority
                data-testid="guide-card-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-card/30 via-transparent to-transparent" />
            </div>

            <div className="p-7 sm:p-9 flex-1 flex flex-col justify-center text-left relative z-10">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <BookOpen className="w-4 h-4 text-primary" />
                </div>
                <span 
                  className="text-xs font-bold uppercase tracking-widest text-primary" 
                  data-testid="guide-card-badge"
                >
                  {t("btn_info_guide")}
                </span>
              </div>

              <h2 
                className="font-display font-semibold text-2xl md:text-3xl text-foreground group-hover:text-primary transition-colors duration-300 leading-tight" 
                data-testid="guide-card-title"
              >
                {t("guide_card_title")}
              </h2>

              <p 
                className="text-muted-foreground mt-4 line-clamp-3 leading-relaxed" 
                data-testid="guide-card-desc"
              >
                {t("guide_card_desc")}
              </p>

              <div 
                className="flex items-center gap-2 mt-6 text-primary font-semibold group-hover:gap-4 transition-all duration-300" 
                data-testid="guide-card-cta"
              >
                {t("guide_card_btn")} 
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </div>
        </NavLink>

        <div className="w-full max-w-2xl mt-8 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-5 opacity-0 animate-reveal-up stagger-4">
          <div className="flex gap-3">
            <span className="text-xl">💡</span>
            <p className="text-sm text-amber-950 dark:text-amber-100 leading-relaxed">
              {t("did_you_know")}
            </p>
          </div>
        </div>

        <aside
          id="ad-container-start"
          className="ad-container w-full max-w-2xl mt-8 min-h-[100px] flex items-center justify-center"
          data-testid="ad-container-start"
          data-ad-slot="start-screen-bottom"
          data-ad-format="horizontal"
          aria-label="Advertisement"
        />
      </div>
    </div>
  );
}
